import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';
import CalculatorForm from './CalculatorForm';

const HomeLoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState([]);

  const calculate = () => {
    const monthlyRate = interestRate / 12 / 100;
    const months = loanTenure * 12;
    const emi = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = emi * months;
    const totalInterest = totalPayment - loanAmount;
    
    // Generate amortization data
    let balance = loanAmount;
    const data = [];
    for (let year = 1; year <= loanTenure; year++) {
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      for (let month = 1; month <= 12; month++) {
        const interest = balance * monthlyRate;
        const principal = emi - interest;
        yearlyInterest += interest;
        yearlyPrincipal += principal;
        balance -= principal;
        if (balance < 0) balance = 0;
      }
      data.push({ year, interest: yearlyInterest, principal: yearlyPrincipal, balance });
    }
    
    setResult({ emi, totalPayment, totalInterest, loanAmount });
    setChartData(data);
  };

  const handleFormSubmit = (userData) => {
    const element = document.getElementById('homeloan-result-content');
    html2pdf().set({ margin: 0.5, filename: `HomeLoan_Report_${userData.name}.pdf` }).from(element).save();
    setShowForm(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-2xl font-bold text-primary mb-2">Home Loan EMI Calculator</h3>
      <p className="text-gray-600 mb-6">Calculate your monthly EMI and total interest payable</p>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div><label className="block font-semibold mb-2">Loan Amount (₹)</label><input type="number" className="input-field" value={loanAmount} onChange={(e) => setLoanAmount(Number(e.target.value))} /></div>
          <div><label className="block font-semibold mb-2">Interest Rate (% p.a.)</label><input type="number" step="0.1" className="input-field" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))} /></div>
          <div><label className="block font-semibold mb-2">Loan Tenure (Years)</label><input type="number" className="input-field" value={loanTenure} onChange={(e) => setLoanTenure(Number(e.target.value))} /></div>
          <button onClick={calculate} className="w-full btn-primary py-3">Calculate EMI</button>
        </div>

        {result && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5">
            <h4 className="font-bold text-lg text-primary mb-4">Loan Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between"><span>Monthly EMI:</span><span className="font-bold text-primary text-xl">₹{result.emi.toFixed(0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span>Total Interest Payable:</span><span>₹{result.totalInterest.toLocaleString()}</span></div>
              <div className="flex justify-between"><span>Total Payment:</span><span>₹{result.totalPayment.toLocaleString()}</span></div>
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm">💡 Tax Benefit: You can claim deduction on interest up to ₹2 lakhs under Section 24(b)</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {chartData.length > 0 && (
        <div className="mt-8">
          <h4 className="font-bold text-lg text-primary mb-4">Year-wise Breakup</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" label={{ value: 'Years', position: 'bottom' }} />
                <YAxis label={{ value: 'Amount (₹)', angle: -90, position: 'left' }} />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend />
                <Area type="monotone" dataKey="interest" stackId="1" stroke="#ffc658" fill="#ffc658" name="Interest" />
                <Area type="monotone" dataKey="principal" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Principal" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div id="homeloan-result-content" style={{ display: 'none' }}>
        <div style={{ padding: '20px' }}>
          <h2>Wisdom Consultancy - Home Loan Report</h2>
          <table><tbody>
            <tr><td>Loan Amount:</td><td>₹{result?.loanAmount.toLocaleString()}</td></tr>
            <tr><td>Monthly EMI:</td><td>₹{result?.emi.toFixed(0).toLocaleString()}</td></tr>
            <tr><td>Total Interest:</td><td>₹{result?.totalInterest.toLocaleString()}</td></tr>
            <tr><td>Total Payment:</td><td>₹{result?.totalPayment.toLocaleString()}</td></tr>
          </tbody></table>
        </div>
      </div>

      {result && (
        <button onClick={() => setShowForm(true)} className="w-full mt-6 bg-green-600 text-white py-3 rounded-lg font-semibold">
          📄 Download Report & Share on WhatsApp
        </button>
      )}
      {showForm && <CalculatorForm onSubmit={handleFormSubmit} onClose={() => setShowForm(false)} calculatorType="Home Loan" />}
    </div>
  );
};

export default HomeLoanCalculator;